'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useUnit } from 'effector-react';
import { NotesList } from '@nx-frontend/features/notes-list';
import { Head } from '@nx-frontend/shared/ui';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $members } from '@nx-frontend/shared/store/members';
import {
  $notes,
  fetchNotesEvent,
  NotesSortBy,
  resetFetchEvent,
  setNotesSortByEvent,
  setNotesSortOrderEvent,
  SortOrder,
} from '@nx-frontend/shared/store/notes';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $orgRoleState } from '@nx-frontend/shared/store/org-role';
import { setBreadcrumbsAllPageVisitedEvent } from '@nx-frontend/shared/store/webnotes-breadcrumbs';
import { $wpCanManageMembers } from '@nx-frontend/shared/store/workspace-access';
import { MenuItemType, NoteDto, PopupType } from '@nx-frontend/shared/types';
import { NoteData, NoteItemType } from '@nx-frontend/shared/types';
import { GetOpenPayWallPopupProps } from '@nx-frontend/shared/types';
import { NextRouterType } from '@nx-frontend/shared/utils';
import { getSortFn } from '@nx-frontend/shared/utils';
import { AllDocsButton, InviteMemberButton } from '@nx-frontend/webnotes-client/shared/ui';
import { getOpenInvitePopup } from '@nx-frontend/widgets/popups';
import { SearchInput } from '@nx-frontend/widgets/search';
import { allDocsButtons } from './all-pages-data';

interface AllPagesProps {
  getOpenPaywallPopup: (props: GetOpenPayWallPopupProps) => void;
}

export function AllPages({ getOpenPaywallPopup }: AllPagesProps) {
  const [
    canManageMembers,
    currentWorkspace,
    members,
    notes,
    orgInfo,
    role,
    billingInfo,
    setBreadcrumbsAllPageVisited,
    currentOrganization,
  ] = useUnit([
    $wpCanManageMembers,
    $currentWorkspace,
    $members,
    $notes,
    $orgInfo,
    $orgRoleState,
    $billingInfo,
    setBreadcrumbsAllPageVisitedEvent,
    $currentOrganization,
  ]);
  const [openModal, fetchNotes, setNotesSortBy, setNotesSortOrder, resetFetch] = useUnit([
    openModalEvent,
    fetchNotesEvent,
    setNotesSortByEvent,
    setNotesSortOrderEvent,
    resetFetchEvent,
  ]);
  const t = useTranslations();
  const lazyLoadingMark = useRef<HTMLDivElement>(null);
  const placeholder = t('webnotes.webnotes-search_pages');

  const showAuthor = members && members.count > 1;
  const sortBy = notes.sortBy;
  const sortOrder = notes.sortOrder;
  const workspaceId = currentWorkspace.globalId;

  const loadMoreNotes = useCallback(
    (order?: SortOrder) => {
      fetchNotes({
        workspaceId: currentWorkspace.globalId,
        type: 'note',
        range: {
          offset: notes.workspaces[currentWorkspace.globalId]?.fetch?.['note-all']?.offset || 0,
          limit: 100,
        },
        rootId: 'root',
        fetchPortalsNotes: false,
        order: [sortBy, order ?? sortOrder],
      });
    },
    [notes.workspaces, currentWorkspace.globalId, sortBy, sortOrder, fetchNotes]
  );

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    if (lazyLoadingMark.current) {
      observer = new IntersectionObserver(
        (entites: IntersectionObserverEntry[]) => {
          const [mark] = entites;
          if (mark.isIntersecting) {
            // once mark is visible on page, load more notes
            loadMoreNotes(sortOrder);
          }
        },
        { root: null, rootMargin: '0px', threshold: 1.0 }
      );
      observer.observe(lazyLoadingMark.current);
    }

    return () => {
      observer?.disconnect();
    };
  }, [sortOrder, loadMoreNotes]);

  useEffect(() => {
    setBreadcrumbsAllPageVisited(true);
  }, [setBreadcrumbsAllPageVisited]);

  const items: NoteData[] =
    Object.values(notes.workspaces[currentWorkspace.globalId]?.entities || {})
      .filter((note) => note.type === 'note' && note.parentId !== 'trash' && !note.inTrash && !note.isSharedForPortal)
      .sort(getSortFn<NoteDto>(sortBy, sortOrder))
      .map((note) => ({
        id: `${NoteItemType.NOTE}#${note.globalId}`,
        type: MenuItemType.NOTE,
        globalId: note.globalId,
        workspaceId: note.workspaceId,
        name: note.title,
        icon: note.emoji || 'page',
        userId: note.userId,
        updatedAt: note.updatedAt,
        createdAt: note.createdAt,
        parentId: `${NoteItemType.NOTES_FOLDER}#${note.parentId}`,
        index: 0,
        visible: true,
        url: '',
        slug: '',
        openInNewTab: false,
        hasChildren: false,
        shared: note.shared,
      })) || [];

  const routerType = NextRouterType.APP;
  const routerPushFn = useRouter().push;

  const handleInviteclick = () => {
    getOpenInvitePopup(
      { orgInfo, role, billingInfo, currentWorkspace, canManageMembers: canManageMembers },
      openModal,
      { routerType, routerPushFn }
    )({
      payloadType: PopupType.INVITE,
      subjectType: 'workspace',
    });
  };

  const handleSortClick = useCallback(
    (type: NotesSortBy) => {
      const order = sortOrder === 'ASC' ? 'DESC' : 'ASC';
      const newOrder = type === sortBy ? order : sortOrder;
      setNotesSortBy(type);
      setNotesSortOrder(newOrder);
      if (sortOrder !== newOrder) {
        resetFetch({ workspaceId, fetchId: 'note-all' });
      }
    },
    [sortOrder, sortBy, workspaceId, setNotesSortBy, setNotesSortOrder, resetFetch]
  );

  const sortIcon = (
    <Icon
      icon={sortOrder === 'ASC' ? IconsMap.ARROW_UP_1 : IconsMap.ARROW_DOWN_1}
      className="w-3 h-3 text-neutral-500"
    />
  );

  const notesFetch = notes?.workspaces[currentWorkspace.globalId]?.fetch || {};
  const pageTitle = `${t('webnotes.webnotes-all_pages')} - ${currentWorkspace.title}`;

  return (
    <div className="flex flex-col w-full h-full">
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <div
        data-cy="allpages-header"
        className="flex w-full px-4 items-center gap-4 border-b-[1px] border-solid border-neutral-200 h-16 box-border"
      >
        <div className="flex w-full justify-between items-center ">
          <div className="flex items-end gap-2 ">
            <Icon icon={IconsMap.DOCUMENT} className="w-6 h-6 text-neutral-500" />
            <span className="text-4 ">{t('webnotes.webnotes-all_pages')}</span>
          </div>
          <div className="flex gap-4 items-center">
            <SearchInput placeholder={placeholder} />
            {canManageMembers && (
              <InviteMemberButton
                onClick={handleInviteclick}
                buttonType="allPage"
                orgRolePrivileges={role.privileges}
              />
            )}
          </div>
        </div>
      </div>
      <div
        data-cy="allpages-content"
        className="flex flex-col items-start gap-6 w-full h-full pt-7 overflow-y-scroll"
        id="main-scrolling-container"
      >
        <div className="flex w-full pl-[41px] pr-[64px] gap-[16px]">
          {allDocsButtons
            ? allDocsButtons.map((button, index) => (
                <AllDocsButton
                  key={index}
                  buttonData={{
                    ...button,
                    title: button.titleLocKey ? t(button.titleLocKey) : button.title,
                    subTitle: button.subTitleLocKey ? t(button.subTitleLocKey) : button.subTitle,
                  }}
                  orgId={currentOrganization.globalId}
                  subscriptionType={orgInfo.subscriptionType}
                  getOpenPaywallPopup={getOpenPaywallPopup}
                />
              ))
            : null}
        </div>
        <div className="pl-[41px] pr-[64px] flex flex-col w-full items-start gap-4">
          {/* <div className="flex items-start pl-4 gap-[20px]">
            <span className="text-[10px] py-1">ALL PAGES</span>
            <span className="text-[10px] py-1">FAVOURITES</span>
            <span className="text-[10px] py-1">CREATED BY ME</span>
            <span className="text-[10px] py-1">SHARED</span>
          </div> */}
          <div className="w-full h-full">
            <table data-cy="allpages-table" className="table w-full">
              <thead data-cy="allpages-table-header" className="table-header-group">
                <tr className="table-row border-b-[1px] border-solid border-neutral-200">
                  <th
                    className="table-cell text-left hover:bg-zinc-50 cursor-pointer"
                    onClick={() => handleSortClick('title')}
                    style={{ width: '51.6%' }}
                  >
                    <div className="py-2.5 px-4 flex items-center gap-1">
                      <span className="text-[10px] font-normal uppercase">{t('webnotes.webnotes-name')}</span>
                      {sortBy === 'title' ? sortIcon : null}
                    </div>
                  </th>
                  {showAuthor ? (
                    <th className="table-cell text-left" style={{ width: '21.7%' }}>
                      <div className="flex items-center p-2">
                        <span className="text-[10px] font-normal uppercase">{t('webnotes.webnotes-author')}</span>
                      </div>
                    </th>
                  ) : null}
                  <th
                    className="table-cell text-left hover:bg-zinc-50 cursor-pointer"
                    style={{ width: '11.1%' }}
                    onClick={() => handleSortClick('updatedAt')}
                  >
                    <div className="p-2 flex items-center gap-1">
                      <span className="text-[10px] font-normal uppercase">{t('webnotes.webnotes-updated')}</span>
                      {sortBy === 'updatedAt' ? sortIcon : null}
                    </div>
                  </th>
                  <th
                    className="table-cell text-left hover:bg-zinc-50 cursor-pointer"
                    style={{ width: '11.1%' }}
                    onClick={() => handleSortClick('createdAt')}
                  >
                    <div className="p-2 flex items-center gap-1">
                      <span className="text-[10px] font-normal uppercase">{t('webnotes.webnotes-created')}</span>
                      {sortBy === 'createdAt' ? sortIcon : null}
                    </div>
                  </th>
                  <th style={{ width: '4.5%' }}></th>
                </tr>
              </thead>

              <tbody className="table-row-group">
                <tr>
                  <td>
                    <div className="table-caption px-4 py-1 mt-4 text-[10px] font-normal text-neutral-500 uppercase">
                      {t('webnotes.webnotes-pages')}
                    </div>
                  </td>
                </tr>
                <NotesList items={items} showAuthor={showAuthor} />
              </tbody>
            </table>
            <div ref={lazyLoadingMark} className="flex flex-col items-center justify-center h-[52px] my-1">
              {notesFetch?.['note-all']?.isFetching && <Spinner />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
